
import { ethers } from 'ethers';
import PumpTokenAbi from '../contracts/abis/PumpToken.json';

export const TOKEN_PAGE_BREAKPOINT = 860;

export const NORMALIZED_SUPPLY_TOKENS = 1_000_000_000;
export const NORMALIZED_VIRTUAL_ETH = 1.435;
export const TRADE_FEE_BPS = 125;
export const BPS_DENOMINATOR = 10_000;
export const TARGET_RAISE_ETH = 4.305;
export const TARGET_MARKET_CAP_ETH = 22.26;

// Backward compatibility alias for legacy curve math
export const CURVE_CONSTANT = NORMALIZED_VIRTUAL_ETH;

type WalletClient = any;

export const getTokenBalance = async (
    tokenAddress: string,
    walletClient: WalletClient,
): Promise<string | null> => {

    if (!walletClient) {
        // console.error("Wallet not connected.");
        return null;
    }

    try {
        const provider = new ethers.BrowserProvider(walletClient.transport);
        const signer = await provider.getSigner();
        const token = new ethers.Contract(tokenAddress, PumpTokenAbi, signer);
        const ownerAddress = await signer.getAddress();
        const tokenBalance = await token.balanceOf(ownerAddress);
        return ethers.formatEther(tokenBalance);
    } catch (error) {
        console.error("Failed to get token balance:", error);
        return null;
    }
}

export const getAccountEthBalance = async (
    walletClient: WalletClient
): Promise<string | null> => {

    if (!walletClient) {
        // console.error("Wallet not connected.");
        return null;
    }
    try {
        const provider = new ethers.BrowserProvider(walletClient.transport);
        const signer = await provider.getSigner();
        const ownerAddress = await signer.getAddress();
        const balanceWei = await provider.getBalance(ownerAddress);
        return ethers.formatEther(balanceWei);
    } catch (error) {
        console.error("Failed to get ETH balance:", error);
        return null;
    }
}

export async function getPepeTokenPrice(retry = 0): Promise<number | null> {
    const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
    
    try {
        const response = await fetch(`${BACKEND_URL}/price-service/latest?limit=1`);
        
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.success && data.data && data.data.length > 0) {
            const latestPrice = data.data[0];
            console.log("Pepe token price from backend:", latestPrice.Price);
            return latestPrice.Price ?? null;
        } else {
            console.log("No price data available from backend");
            return null;
        }

    } catch (error) {
        console.error("Failed to fetch Pepe token price from backend:", error);
        
        // Fallback to Coingecko if backend fails
        if (retry < 2) {
            console.warn(`Backend failed. Retrying with Coingecko fallback...`);
            return await getPepeTokenPriceFallback(retry + 1);
        }
        
        return null;
    }
}

// Fallback function using Coingecko API
async function getPepeTokenPriceFallback(retry = 0): Promise<number | null> {
    const url = "https://api.coingecko.com/api/v3/simple/price?ids=pepe&vs_currencies=usd";

    try {
        const resp = await fetch(url);

        if (resp.status === 429) {
            // Rate limited
            if (retry < 3) {
                const delay = (2 ** retry) * 1000; // exponential backoff
                console.warn(`Rate limited. Retrying in ${delay / 1000}s...`);
                await new Promise(res => setTimeout(res, delay));
                return getPepeTokenPriceFallback(retry + 1);
            }
            throw new Error("Rate limit exceeded. Try again later.");
        }

        if (!resp.ok) {
            throw new Error(`HTTP error! status: ${resp.status}`);
        }

        const data = await resp.json();
        const price = data?.pepe?.usd;
        console.log("Pepe token price from Coingecko fallback:", price);
        return price ?? null;

    } catch (error) {
        console.error("Failed to fetch Pepe token price from Coingecko fallback:", error);
        return null;
    }
}

export function startPepeTokenPriceUpdates(callback: (price: number | null) => void): NodeJS.Timeout {
    getPepeTokenPrice().then(callback);
    const intervalId = setInterval(async () => {
        const price = await getPepeTokenPrice();
        callback(price);
    }, 10000); // 10 seconds (backend updates every 5 seconds, so we check every 10 seconds)
    return intervalId;
}


export function stopPepeTokenPriceUpdates(intervalId: NodeJS.Timeout): void {
    clearInterval(intervalId);
}

